'use client';

import * as React from 'react';
import { ImageOff, Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useOffer } from '@/lib/api/offers';
import { formatMoney } from '@/lib/money';

interface SetupUpsellOfferRowProps {
  offerId: string;
  onRemove: () => void;
  isRemoving: boolean;
}

/** Local view of the offer-detail fields we render (published type omits some). */
interface OfferLineItemView {
  quantity?: number | null;
  price?: {
    currencyOptions?: Record<string, { amount?: number } | null> | null;
    variant?: {
      imageUrl?: string | null;
      product?: { name?: string | null } | null;
    } | null;
  } | null;
}

function getPrice(lineItems: OfferLineItemView[]): string | null {
  let total = 0;
  let currency = '';
  for (const li of lineItems) {
    const options = li.price?.currencyOptions;
    if (!options) continue;
    const cur = Object.keys(options)[0];
    const amount = cur ? options[cur]?.amount : undefined;
    if (cur && typeof amount === 'number') {
      total += amount * (li.quantity || 1);
      if (!currency) currency = cur.toUpperCase();
    }
  }
  if (total <= 0 || !currency) return null;
  try {
    return formatMoney(total, currency);
  } catch {
    return null;
  }
}

/** One created upsell offer in the setup list — image, name, price, remove. */
export function SetupUpsellOfferRow({ offerId, onRemove, isRemoving }: SetupUpsellOfferRowProps) {
  const { t } = useTranslation('setup');
  const { data, isLoading } = useOffer(offerId);
  const offer = data?.offer;

  const lineItems = (offer?.offerLineItems ?? []) as OfferLineItemView[];
  const title = (offer?.titleTrans as Record<string, string> | null | undefined)?.en || offerId;
  const imageUrl = lineItems[0]?.price?.variant?.imageUrl ?? null;
  const productName = lineItems[0]?.price?.variant?.product?.name ?? null;
  const price = getPrice(lineItems);

  return (
    <div className="border-border flex items-center gap-3 rounded-lg border px-3 py-2.5">
      <div className="bg-muted flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border">
        {isLoading ? (
          <Loader2 className="text-muted-foreground size-4 animate-spin" aria-hidden />
        ) : imageUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={imageUrl} alt={productName ?? title} className="size-full object-cover" />
        ) : (
          <ImageOff className="text-muted-foreground/40 size-4" aria-hidden />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="m-0 truncate text-sm font-medium">{title}</p>
        {productName ? (
          <p className="text-muted-foreground m-0 truncate text-xs">{productName}</p>
        ) : null}
      </div>

      {price ? <span className="shrink-0 text-sm font-semibold tabular-nums">{price}</span> : null}

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="text-muted-foreground hover:text-destructive size-8 shrink-0"
        onClick={onRemove}
        disabled={isRemoving}
        aria-label={t('experience.checkout.upsellOffer.remove')}
      >
        {isRemoving ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : (
          <Trash2 className="size-4" aria-hidden />
        )}
      </Button>
    </div>
  );
}
